
import { Badge } from "@/components/ui/badge";
import {
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import { Clock, Filter, Pill, Search, Star } from "lucide-react";
import { Product } from "@/types/product";

interface SearchResultsProps {
  value: string;
  filteredProducts: Product[];
  recentSearches: string[];
  prescriptionCount: number;
  otcCount: number;
  setPrescriptionFilter: (value: boolean | null) => void;
  setOpen?: (open: boolean) => void;
}

export function SearchResults({
  value,
  filteredProducts,
  recentSearches,
  prescriptionCount,
  otcCount,
  setPrescriptionFilter,
  setOpen = () => {},
}: SearchResultsProps) {
  const handleSelect = (callback: () => void) => {
    callback();
    // Close the dialog after selection if needed
    // setOpen(false);
  };

  return (
    <CommandList>
      <CommandEmpty>
        <div className="py-6 text-center">
          <p>No results found.</p>
          <p className="text-xs text-muted-foreground mt-1">Try a different search term.</p>
        </div>
      </CommandEmpty>
      
      {value.length === 0 && (
        <CommandGroup heading="Recent Searches">
          {recentSearches.map((search) => (
            <CommandItem 
              key={search}
              onSelect={() => handleSelect(() => {})}
              className="flex items-center"
            >
              <Clock className="mr-2 h-4 w-4" aria-hidden="true" />
              <span>{search}</span>
              <Star 
                className="ml-auto h-4 w-4 text-muted-foreground hover:text-yellow-400 cursor-pointer" 
                aria-label="Save to favorites"
                onClick={(e) => {
                  e.stopPropagation();
                  // Add to favorites logic would go here
                }}
              />
            </CommandItem>
          ))}
        </CommandGroup>
      )}
      
      <CommandGroup heading="Quick Filters">
        <CommandItem 
          onSelect={() => handleSelect(() => setPrescriptionFilter(true))}
          className="flex items-center cursor-pointer"
        >
          <Pill className="mr-2 h-4 w-4" aria-hidden="true" />
          <span>Prescription Only</span>
          <Badge variant="secondary" className="ml-auto">{prescriptionCount}</Badge>
        </CommandItem>
        <CommandItem 
          onSelect={() => handleSelect(() => setPrescriptionFilter(false))}
          className="flex items-center cursor-pointer"
        >
          <Filter className="mr-2 h-4 w-4" aria-hidden="true" />
          <span>Over the Counter</span>
          <Badge variant="secondary" className="ml-auto">{otcCount}</Badge>
        </CommandItem>
      </CommandGroup>
      
      <CommandSeparator />
      
      {filteredProducts.length > 0 && (
        <CommandGroup heading={`Results (${filteredProducts.length})`}>
          {filteredProducts.map((product) => (
            <CommandItem 
              key={product.title}
              onSelect={() => handleSelect(() => {
                // Navigate to product detail or show product details
                window.location.href = `/product/${product.id}`;
              })}
              className="flex items-center cursor-pointer"
            >
              <Search className="mr-2 h-4 w-4" aria-hidden="true" />
              <span>{product.title}</span>
              <span className="ml-2 text-muted-foreground">
                {product.price}
              </span>
              <Badge 
                variant={product.prescription ? "destructive" : "secondary"}
                className="ml-auto"
              >
                {product.prescription ? "Rx" : "OTC"}
              </Badge>
            </CommandItem>
          ))}
        </CommandGroup>
      )}
    </CommandList>
  );
}
